import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api, { setToken } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

export default function ConfirmEmailPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setSession } = useAuth();
    const token = searchParams.get('token');
    const [status, setStatus] = useState('loading'); // loading, success, expired, error
    const [message, setMessage] = useState('');
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Lien de confirmation invalide.');
            return;
        }
        confirmEmail();
    }, [token]);

    const confirmEmail = async () => {
        try {
            const response = await api.post('/auth/confirm-email', { token });
            
            // Auto-login with the returned token
            if (response.data.access_token && response.data.user) {
                setSession(response.data.access_token, response.data.user);
            }
            
            setStatus('success');
            setMessage('Votre compte a été confirmé avec succès !');
            
            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
                navigate('/admin', { replace: true });
            }, 2000);
            
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (typeof detail === 'object' && detail.code === 'TOKEN_EXPIRED') {
                setStatus('expired');
                setMessage(detail.message);
                setUserId(detail.user_id);
            } else {
                setStatus('error');
                setMessage(detail?.message || detail || 'Erreur lors de la confirmation.');
            }
        }
    };

    const handleResend = async () => {
        try {
            setStatus('loading');
            // We need the email - redirect to resend page
            navigate('/resend-confirmation');
        } catch (error) {
            setStatus('error');
            setMessage('Erreur lors de l\'envoi.');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#681793]/5 to-[#FF914D]/5 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    {status === 'loading' && (
                        <>
                            <Loader2 className="w-16 h-16 mx-auto mb-4 text-[#681793] animate-spin" />
                            <CardTitle>Vérification en cours...</CardTitle>
                        </>
                    )}
                    {status === 'success' && (
                        <>
                            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                            <CardTitle className="text-green-600">Email confirmé !</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                    {status === 'expired' && (
                        <>
                            <XCircle className="w-16 h-16 mx-auto mb-4 text-orange-500" />
                            <CardTitle className="text-orange-600">Lien expiré</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                    {status === 'error' && (
                        <>
                            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                            <CardTitle className="text-red-600">Erreur</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                </CardHeader>
                <CardContent className="space-y-4">
                    {status === 'success' && (
                        <p className="text-center text-muted-foreground">
                            Redirection vers votre tableau de bord...
                        </p>
                    )}
                    {status === 'expired' && (
                        <Button 
                            onClick={handleResend} 
                            className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D]"
                        >
                            <Mail className="w-4 h-4 mr-2" />
                            Renvoyer un nouveau lien
                        </Button>
                    )}
                    {(status === 'error' || status === 'expired') && (
                        <Link to="/login">
                            <Button variant="outline" className="w-full">
                                Retour à la connexion
                            </Button>
                        </Link>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
